import { Helmet } from "react-helmet-async";
import { useParams, Navigate, Link } from "react-router-dom";
import { PageHero } from "@/components/shared/PageHero";
import { CTASection } from "@/components/shared/CTASection";
import { serviceAreaCities } from "@/data/serviceAreas";
import { MapPin, CheckCircle, ArrowRight, Phone, Leaf, Snowflake, Flower2, Wind } from "lucide-react";
import { Button } from "@/components/ui/button";

const services = [
  { icon: Leaf, name: "Lawn Care & Mowing", link: "/services/lawn-care" },
  { icon: Wind, name: "Aeration & Overseeding", link: "/services/aeration" },
  { icon: Flower2, name: "Mulching & Beds", link: "/services/mulching" },
  { icon: Leaf, name: "Dethatching", link: "/services/dethatching" },
  { icon: Leaf, name: "Spring & Fall Cleanups", link: "/services/spring-fall-cleanups" },
  { icon: Snowflake, name: "Snow Removal", link: "/services/snow-removal" },
];

const CityPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const city = serviceAreaCities.find(c => c.slug === slug);

  if (!city) {
    return <Navigate to="/service-areas" replace />;
  }

  return (
    <>
      <Helmet>
        <title>{city.name} Lawn Care & Landscaping | Riekels Lawn Care</title>
        <meta
          name="description"
          content={`Professional lawn care, landscaping, and snow removal services in ${city.name}, MN. Locally owned and operated. Call (763) 245-7813 for a free estimate.`}
        />
      </Helmet>
      <main>
        <PageHero
          badge={`Serving ${city.name}, MN`}
          title={`Lawn Care & Landscaping in ${city.name}`}
          subtitle={`Trusted by ${city.name} homeowners for professional lawn care and landscaping services.`}
        />

        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              {/* Main Content */}
              <div className="lg:col-span-2 space-y-12">
                {/* About the Area */}
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="icon-circle">
                      <MapPin className="w-6 h-6" />
                    </div>
                    <h2 className="text-3xl font-heading font-bold text-foreground">
                      About {city.name}
                    </h2>
                  </div>
                  <p className="text-muted-foreground leading-relaxed text-lg mb-6">
                    {city.localInfo.about}
                  </p>
                  
                  {/* Landmarks */}
                  <div className="bg-section-alt rounded-xl p-6 mb-8">
                    <h3 className="text-xl font-heading font-bold text-foreground mb-4">
                      Local Landmarks We Know & Love
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {city.localInfo.landmarks.map((landmark, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0" />
                          <span className="text-muted-foreground">{landmark}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Neighborhoods */}
                  {city.localInfo.neighborhoods && (
                    <div className="bg-card rounded-xl border border-border p-6">
                      <h3 className="text-xl font-heading font-bold text-foreground mb-4">
                        Neighborhoods We Serve in {city.name}
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {city.localInfo.neighborhoods.map((neighborhood, idx) => (
                          <span 
                            key={idx} 
                            className="bg-secondary/10 text-secondary px-3 py-1 rounded-full text-sm font-medium"
                          >
                            {neighborhood}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Why Choose Us */}
                <div>
                  <h2 className="text-3xl font-heading font-bold text-foreground mb-6">
                    Why {city.name} Homeowners Choose Riekels
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="feature-card">
                      <h3 className="font-heading font-bold text-foreground mb-2">Locally Owned & Operated</h3>
                      <p className="text-muted-foreground text-sm">We live and work in the community we serve, treating every lawn like our own.</p>
                    </div>
                    <div className="feature-card">
                      <h3 className="font-heading font-bold text-foreground mb-2">Fully Insured</h3>
                      <p className="text-muted-foreground text-sm">Licensed and insured for your peace of mind on every project.</p>
                    </div>
                    <div className="feature-card">
                      <h3 className="font-heading font-bold text-foreground mb-2">Free Estimates</h3>
                      <p className="text-muted-foreground text-sm">No-obligation quotes with transparent pricing. No hidden fees or surprises.</p>
                    </div>
                    <div className="feature-card">
                      <h3 className="font-heading font-bold text-foreground mb-2">Quality Equipment</h3>
                      <p className="text-muted-foreground text-sm">Professional-grade equipment maintained for optimal performance and results.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <div className="lg:col-span-1">
                <div className="sticky top-24 space-y-6">
                  {/* Contact Card */}
                  <div className="bg-primary text-primary-foreground rounded-xl p-6">
                    <h3 className="text-xl font-heading font-bold mb-4">
                      Get a Free Estimate in {city.name}
                    </h3>
                    <p className="text-primary-foreground/90 mb-6">
                      Ready to transform your lawn? We offer free, no-obligation estimates for all {city.name} homeowners.
                    </p>
                    <div className="space-y-3">
                      <a
                        href="https://form.fillout.com/t/aUQUhSyfAXus"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block"
                      >
                        <Button className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground font-semibold">
                          Request Free Quote <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </a>
                      <a href="tel:+17632457813" className="block">
                        <Button variant="outline" className="w-full border-white/50 bg-white/10 text-white hover:bg-white/20">
                          <Phone className="w-4 h-4 mr-2" /> (763) 245-7813
                        </Button>
                      </a>
                    </div>
                  </div>

                  {/* Services Card */}
                  <div className="bg-card rounded-xl border border-border p-6">
                    <h3 className="text-lg font-heading font-bold text-foreground mb-4">
                      Our Services in {city.name}
                    </h3>
                    <div className="space-y-2">
                      {services.map((service, idx) => (
                        <Link
                          key={idx}
                          to={service.link}
                          className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors group"
                        >
                          <service.icon className="w-5 h-5 text-secondary" />
                          <span className="text-foreground group-hover:text-secondary transition-colors">
                            {service.name}
                          </span>
                        </Link>
                      ))}
                    </div>
                  </div>

                  {/* Back to Service Areas */}
                  <Link to="/service-areas" className="flex items-center gap-2 text-muted-foreground hover:text-secondary transition-colors">
                    <ArrowRight className="w-4 h-4 rotate-180" />
                    View All Service Areas
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <CTASection />
      </main>
    </>
  );
};

export default CityPage;
